import { ProfileCountsBadge } from '@/features/candidate-profile/profile-section-counts'
import { SendBookingLinkDialog } from '@/features/interview-scheduling/send-booking-link-dialog'
import { useAuth } from '@/lib'
import {
	Glyphs,
	JobiLoader,
	PipelineBadge,
	SlideOverPanel,
	TimeAgo,
	Tooltip,
	WidgetInputQuickSwitch,
	widgetInputQuickSwitchDivider,
} from '@/ui'
import { LinkLocalProps, logger } from '@/ui'
import { CandidateModel } from '@/ui'
import { CampaignNav } from '@/ui'
import { CampaignCandidatesViewModel } from '@/ui'
import { useWhatsAppEnabled } from '@/ui/utilities/hooks/use-whatsapp-enabled'
import ctl from '@netlify/classnames-template-literals'
import { useAtomValue, useSetAtom } from 'jotai'
import { MailIcon, MessageCircleIcon } from 'lucide-react'
import { motion } from 'motion/react'
import React, { Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BulkActionsBar } from './bulk-actions-bar'
import { BulkEmailModal } from './bulk-email-modal'
import { BulkWhatsAppModal } from './bulk-whatsapp-modal'
import { CampaignCandidatesKanban } from './campaign-candidates-kanban'
import { CampaignCandidatesToolbar, type SortOption, type ViewMode } from './campaign-candidates-toolbar'
import { NotesBadge } from './notes-badge'
import { PipelineResetModal, StageToRemove } from './pipeline-reset-modal'
import {
	clearSelectionAtom,
	getGroupSelectionState,
	selectAllAtom,
	selectedCandidateIdsAtom,
	selectedCountAtom,
	toggleCandidateSelectionAtom,
} from './selection-atoms'
import { SelectionCheckbox } from './selection-checkbox'

// Minimal LCH interpolation for red → yellow → green

type LCH = [number, number, number]

// Precomputed LCH values (approx, from sRGB → LCH conversion)
const RED: LCH = [54.29, 106.84, 40.85]
const YELLOW: LCH = [97.14, 94.93, 102.85]
const GREEN: LCH = [87.82, 119.78, 136.02]

// LCH → sRGB
function lchToRgb([l, c, h]: LCH): [number, number, number] {
	const hr = (h * Math.PI) / 180
	const a = Math.cos(hr) * c
	const b = Math.sin(hr) * c

	// Lab → XYZ
	const fy = (l + 16) / 116
	const fx = fy + a / 500
	const fz = fy - b / 200
	const Xn = 0.95047, Yn = 1.0, Zn = 1.08883

	function finv(t: number) {
		const t3 = t ** 3
		return t3 > 0.008856 ? t3 : (116 * t - 16) / 903.3
	}

	const x = Xn * finv(fx)
	const y = Yn * finv(fy)
	const z = Zn * finv(fz)

	// XYZ → linear RGB
	const rl = x * 3.2406 + y * -1.5372 + z * -0.4986
	const gl = x * -0.9689 + y * 1.8758 + z * 0.0415
	const bl = x * 0.0557 + y * -0.2040 + z * 1.0570

	function gamma(v: number) {
		return v <= 0.0031308
			? 12.92 * v
			: 1.055 * Math.pow(v, 1 / 2.4) - 0.055
	}

	return [
		Math.round(Math.min(255, Math.max(0, gamma(rl) * 255))),
		Math.round(Math.min(255, Math.max(0, gamma(gl) * 255))),
		Math.round(Math.min(255, Math.max(0, gamma(bl) * 255))),
	]
}

// LCH interpolate (handles hue wraparound)
function mixLch(c1: LCH, c2: LCH, t: number): LCH {
	const [l1, c1c, h1] = c1
	const [l2, c2c, h2] = c2
	let dh = h2 - h1
	if (dh > 180) { dh -= 360 }
	if (dh < -180) { dh += 360 }
	return [
		l1 + (l2 - l1) * t,
		c1c + (c2c - c1c) * t,
		h1 + dh * t,
	]
}

export function getScoreBackgroundColor(percentage: number): string {
	const t = Math.max(0, Math.min(100, percentage)) / 100

	const lch = t <= 0.5
		? mixLch(RED, YELLOW, t * 2)
		: mixLch(YELLOW, GREEN, (t - 0.5) * 2)

	const [r, g, b] = lchToRgb(lch)
	return `rgba(${r}, ${g}, ${b}, 0.3)`
}

const stageBackgroundColors: Record<string, string> = {
	New: 'var(--color-lime-500)',
	Call: 'var(--color-cyan-500)',
	Interview: 'var(--color-purple-500)',
	Offer: 'var(--color-yellow-500)',
	Hired: 'var(--color-green-500)',
	Rejected: 'var(--color-red-500)',
}

const defaultStageColor = 'var(--color-gray-500)'

function getStageBackgroundColor(stageName: string): string {
	return stageBackgroundColors[stageName] ?? defaultStageColor
}

function sortCandidates(
	candidates: CandidateModel[],
	sortOption: SortOption,
): CandidateModel[] {
	const [field, direction] = sortOption.split('-') as ['name' | 'score' | 'applied', 'asc' | 'desc']
	const dir = direction === 'asc' ? 1 : -1

	return [...candidates].sort((a, b) => {
		switch (field) {
			case 'name': {
				const nameA = a.name?.toLowerCase() ?? ''
				const nameB = b.name?.toLowerCase() ?? ''
				return nameA.localeCompare(nameB) * dir
			}
			case 'score': {
				const scoreA = a.score?.percentage ?? -1
				const scoreB = b.score?.percentage ?? -1
				return (scoreA - scoreB) * dir
			}
			case 'applied': {
				const timeA = a.createdAt?.getTime() ?? 0
				const timeB = b.createdAt?.getTime() ?? 0
				return (timeA - timeB) * dir
			}
			default:
				return 0
		}
	})
}

export function CampaignCandidates(
	{
		children,
		view,
		loading,
		errors,
		events: {
			onCsvExport,
			onCandidateMove,
			onCandidatesRefresh,
			onCandidateClick,
			onSlideOverClose,
			onStageRename,
			onStageAdd,
			onStageDelete,
			onStagesReorder,
			onResetToDefaults,
			onResetModalClose,
		},
		renderers,
		organisationDefaults,
	}: CampaignCandidatesViewModel,
) {
	const { t } = useTranslation('common')
	const isWhatsappEnabled = useWhatsAppEnabled()
	const { can } = useAuth()
	const canEditPipeline = can('pipeline-stage:update')

	const [editMode, setEditMode] = useState(false)
	const selectedIds = useAtomValue(selectedCandidateIdsAtom)
	const selectedCount = useAtomValue(selectedCountAtom)
	const clearSelection = useSetAtom(clearSelectionAtom)
	const [showBulkEmailModal, setShowBulkEmailModal] = useState(false)
	const [showBulkWhatsAppModal, setShowBulkWhatsAppModal] = useState(false)
	const [showSendBookingLinkModal, setShowSendBookingLinkModal] = useState(false)
	const [sendBookingLinkCandidateIds, setSendBookingLinkCandidateIds] = useState<string[]>([])

	const handleBulkMove = useCallback((stageId: string) => {
		onCandidateMove(Array.from(selectedIds), stageId)
	}, [selectedIds, onCandidateMove])

	const handleBulkEmail = useCallback(() => {
		setShowBulkEmailModal(true)
	}, [])

	const handleBulkEmailSuccess = useCallback(() => {
		clearSelection()
	}, [clearSelection])

	const handleBulkWhatsApp = useCallback(() => {
		setShowBulkWhatsAppModal(true)
	}, [])

	const handleBulkWhatsAppSuccess = useCallback(() => {
		clearSelection()
	}, [clearSelection])

	const handleBulkSendBookingLink = useCallback(() => {
		// Snapshot the recipients now; selectedIds is cleared on success/Escape and the dialog
		// must keep sending to who was selected when it opened.
		setSendBookingLinkCandidateIds(Array.from(selectedIds))
		setShowSendBookingLinkModal(true)
	}, [selectedIds])

	const handleBulkSendBookingLinkSuccess = useCallback(() => {
		clearSelection()
	}, [clearSelection])

	const sourceStages = useMemo(() => {
		const stageIds = new Set(
			view.candidates
				.filter(c => selectedIds.has(c.id))
				.map(c => c.stageId),
		)
		return view.stages
			.filter(s => stageIds.has(s.id))
			.map(s => s.name)
	}, [view.candidates, view.stages, selectedIds])

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key !== 'Escape') {
				return
			}

			if (selectedCount > 0) {
				clearSelection()
				return
			}

			if (editMode) {
				setEditMode(false)
				return
			}
		}
		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [selectedCount, editMode, clearSelection])

	const [viewMode, setViewMode] = useState<ViewMode>(() => {
		const stored = localStorage.getItem('candidates:view-mode')
		// Support legacy 'grid' value from localStorage
		if (stored === 'table') { return 'table' }
		return 'swimlanes'
	})

	const handleViewModeChange = (mode: ViewMode) => {
		setViewMode(mode)
		localStorage.setItem('candidates:view-mode', mode)
	}
	const [sortOption, setSortOption] = useState<SortOption>('applied-desc')
	const [groupByStages, setGroupByStages] = useState(true)
	const [showResetModal, setShowResetModal] = useState(false)

	const countCustomStages = useCallback(() => {
		return view.stages.filter(s => !s.locked).length
	}, [view.stages])

	const handleEditModeChange = useCallback((editing: boolean) => {
		if (!canEditPipeline) { return }
		setEditMode(editing)
		logger.track(editing ? 'pipeline:edit_mode_entered' : 'pipeline:edit_mode_exited', {
			data: {
				campaign_id: view.campaign.id,
				total_custom_stages: countCustomStages(),
			},
		})
	}, [view.campaign.id, countCustomStages, canEditPipeline])

	const handleStageRename = useCallback((stageId: string, newName: string) => {
		const stage = view.stages.find(s => s.id === stageId)
		if (stage && !stage.locked) {
			logger.track('pipeline:stage_renamed', {
				data: {
					campaign_id: view.campaign.id,
					stage_name: newName,
					previous_name: stage.name,
				},
			})
		}
		onStageRename?.(stageId, newName)
	}, [view.campaign.id, view.stages, onStageRename])

	const handleStageAdd = useCallback((afterStageId: string, name: string) => {
		logger.track('pipeline:stage_added', {
			data: {
				campaign_id: view.campaign.id,
				stage_name: name,
				total_custom_stages: countCustomStages() + 1,
			},
		})
		onStageAdd?.(afterStageId, name)
	}, [view.campaign.id, countCustomStages, onStageAdd])

	const handleStageDelete = useCallback(async (stageId: string, migrateCandidatesTo?: string) => {
		const stage = view.stages.find(s => s.id === stageId)
		const deleted = await onStageDelete?.(stageId, migrateCandidatesTo)
		// Track only a real deletion — a duplicate/in-flight delete resolves to false.
		if (deleted && stage) {
			logger.track('pipeline:stage_deleted', {
				data: {
					campaign_id: view.campaign.id,
					stage_name: stage.name,
					had_candidates: (view.candidateCounts[stageId] ?? 0) > 0,
					total_custom_stages: countCustomStages() - 1,
				},
			})
		}
	}, [view.campaign.id, view.stages, view.candidateCounts, countCustomStages, onStageDelete])

	const handleStagesReorder = useCallback((stages: Array<{ id: string; name: string; locked?: boolean }>) => {
		// Find which stage moved by comparing old vs new positions
		const oldPositions = new Map(view.stages.map((s, i) => [s.id, i]))
		const newPositions = new Map(stages.map((s, i) => [s.id, i]))

		for (const stage of stages) {
			const oldPos = oldPositions.get(stage.id)
			const newPos = newPositions.get(stage.id)
			if (oldPos !== undefined && newPos !== undefined && oldPos !== newPos) {
				logger.track('pipeline:stage_reordered', {
					data: {
						campaign_id: view.campaign.id,
						stage_name: stage.name,
						from_position: oldPos,
						to_position: newPos,
					},
				})
				break // Only track the moved stage
			}
		}
		onStagesReorder?.(stages)
	}, [view.campaign.id, view.stages, onStagesReorder])

	// Compute stages to remove when resetting to defaults
	// Compare by NAME since campaign stages have different IDs than org defaults
	const stagesToRemove: StageToRemove[] = useMemo(() => {
		if (!organisationDefaults?.stages) { return [] }

		const defaultNames = new Set(organisationDefaults.stages.map(s => s.name))
		return view.stages
			.filter(s => !defaultNames.has(s.name) && !s.locked)
			.map(s => ({
				id: s.id,
				name: s.name,
				candidateCount: view.candidateCounts[s.id] ?? 0,
			}))
	}, [view.stages, view.candidateCounts, organisationDefaults?.stages])

	// Target stages for migration: all org default stages (excluding Rejected)
	// We use stage NAMES since stages will be created before migration if needed
	const resetTargetStages = useMemo(() => {
		if (!organisationDefaults?.stages) { return [] }
		return organisationDefaults.stages
			.filter(s => s.name !== 'Rejected')
			.map((s, index) => ({
				id: s.name, // Use name as ID since we'll map by name
				name: s.name,
				order: index,
				locked: s.locked,
			}))
	}, [organisationDefaults?.stages])

	const handleResetClick = useCallback(() => {
		setShowResetModal(true)
	}, [])

	const handleResetModalClose = useCallback(() => {
		setShowResetModal(false)
		onResetModalClose?.()
	}, [onResetModalClose])

	const handleResetConfirm = useCallback((migrations: Record<string, string>) => {
		if (!canEditPipeline || !onResetToDefaults) { return }
		// Backend handles determining which stages to remove
		// Close modal and exit edit mode after successful reset
		onResetToDefaults(migrations, () => {
			setShowResetModal(false)
			setEditMode(false)
		})
	}, [onResetToDefaults, canEditPipeline])

	const onStageChange = (candidateIds: string[], stageId: string) => {
		const toStage = view.stages.find((stage: { id: string; name: string }) => stage.id === stageId)
		if (!toStage) {
			logger.error(new Error(`Stage ${stageId} not found, unable to move candidate`), {
				category: 'candidates',
				data: {
					candidateIds,
					stageId,
					availableStages: view.stages.map((s: { id: string; name: string }) => ({ id: s.id, name: s.name })),
				},
			})
			throw new Error(`Stage ${stageId} not found, unable to move candidate`)
		}

		// Bulk moves are tracked server-side via "Candidates: Bulk Moved"
		if (candidateIds.length === 1) {
			const candidate = view.candidates.find(c => c.id === candidateIds[0])
			const fromStageName = view.stages.find((s: { id: string; name: string }) => s.id === candidate?.stageId)
				?.name

			logger.track('candidates:candidate_move_stage', {
				data: {
					trigger: 'table_badge',
					candidate_id: candidateIds[0],
					from_stage: fromStageName,
					to_stage: toStage.name,
				},
			})
		}

		onCandidateMove(candidateIds, stageId)
	}

	const [isRefreshing, setIsRefreshing] = useState(false)
	const handleCandidatesRefresh = () => {
		setIsRefreshing(true)
		onCandidatesRefresh().then(() => {
			setIsRefreshing(false)
		})
	}

	const sortedCandidates = useMemo(
		() => sortCandidates(view.candidates, sortOption),
		[view.candidates, sortOption],
	)

	// Filter to stages that have candidates, preserving API order
	const orderedStages = useMemo(() => {
		return view.stages.filter((stage) => sortedCandidates.some((candidate) => candidate.stageId === stage.id))
	}, [view.stages, sortedCandidates])

	const visibleCandidateIds = useMemo(() => sortedCandidates.map((c) => c.id), [sortedCandidates])
	const selectAll = useSetAtom(selectAllAtom)
	const visibleSelection = useMemo(
		() => getGroupSelectionState(visibleCandidateIds, selectedIds),
		[visibleCandidateIds, selectedIds],
	)

	return (
		<CampaignNav activeCampaignNavLink="candidates" fullWidth>
			<div className="min-h-[inherit]">
				<div className="grid grid-rows-[auto_1fr] h-[var(--screen-height)] overflow-y-auto isolate w-full items-start">
					<CampaignCandidatesToolbar
						viewMode={viewMode}
						onViewModeChange={handleViewModeChange}
						sortOption={sortOption}
						onSortChange={setSortOption}
						groupByStages={groupByStages}
						onGroupByStagesChange={setGroupByStages}
						isRefreshing={isRefreshing}
						onRefresh={handleCandidatesRefresh}
						canExport={view.candidates.length > 0 && can('submission:export')}
						onExport={onCsvExport}
						unreadEmailCount={view.unreadEmailCount}
						unreadWhatsAppCount={view.unreadWhatsAppCount}
						editMode={editMode}
						onEditModeChange={handleEditModeChange}
						canEditPipeline={canEditPipeline}
						onResetToDefaults={canEditPipeline && onResetToDefaults ? handleResetClick : undefined}
					/>

					{loading.candidates
						? (
							<div className="text-sm text-grey-500 flex items-center justify-center h-full py-4">
								<div className="flex items-center flex-col gap-6 max-w-[350px]">
									<JobiLoader />
								</div>
							</div>
						)
						: view.candidates.length === 0
						? (
							<div className="text-sm text-grey-500 flex items-center justify-center h-full py-4">
								<div className="flex items-center flex-col gap-6 max-w-[350px]">
									<div className="scale-200 text-brand-400">
										<Glyphs.ultimate.WineIcon />
									</div>
									{view.campaign.status === 'review'
										? (
											<div className="text-sm text-grey-500 text-center">
												{t('candidates.empty.inReview')}
											</div>
										)
										: (
											<div className="text-sm text-grey-500">
												{t('candidates.empty.awaiting')}
											</div>
										)}
								</div>
							</div>
						)
						: viewMode === 'swimlanes'
						? (
							<div className="h-full">
								<CampaignCandidatesKanban
									view={view}
									sortOption={sortOption}
									onCandidateMove={onCandidateMove}
									onCandidateClick={onCandidateClick}
									editMode={canEditPipeline && editMode}
									onStageRename={handleStageRename}
									onStageAdd={handleStageAdd}
									onStageDelete={handleStageDelete}
									onStagesReorder={handleStagesReorder}
									isDeleting={loading.deleting}
								/>
							</div>
						)
						: (
							<div className="py-4">
								<table className="min-w-full divide-y divide-gray-300 text-sm text-grey-800">
									<motion.thead>
										<tr>
											<th scope="col" className="py-3.5 pl-4 pr-2 w-px">
												<SelectionCheckbox
													checked={visibleSelection.checked}
													indeterminate={visibleSelection.indeterminate}
													onToggle={() => selectAll(visibleCandidateIds)}
													label={t('bulkActions.selectAll')}
													alwaysVisible
												/>
											</th>
											<TableHeader>{t('candidates.tableHeaders.stage')}</TableHeader>
											<TableHeader>{t('candidates.tableHeaders.name')}</TableHeader>
											<TableHeader>{t('candidates.tableHeaders.email')}</TableHeader>
											<TableHeader>{t('candidates.tableHeaders.phone')}</TableHeader>
											<TableHeader>{t('candidates.tableHeaders.score')}</TableHeader>
											<TableHeader>{t('candidates.tableHeaders.submitted')}</TableHeader>
											<th></th>
										</tr>
									</motion.thead>
									{groupByStages
										? (
											orderedStages.map((stage: { id: string; name: string }) => (
												<Fragment key={stage.id}>
													<motion.tbody
														layoutId={`stage-${stage.id}`}
														className="divide-y divide-gray-200 relative bg-white"
													>
														<motion.tr
															layout="position"
															className="absolute inset-y-0 left-0 w-2 z-100 flex items-center justify-center"
															style={{
																backgroundColor: getStageBackgroundColor(stage.name),
															}}
														>
															<motion.td
																layout="position"
																className="text-xs text-white font-medium [writing-mode:sideways-lr]"
															>
															</motion.td>
														</motion.tr>
														<TableBody
															view={view}
															renderers={renderers}
															candidates={sortedCandidates.filter((candidate) =>
																candidate.stageId === stage.id
															)}
															stages={view.stages}
															onStageChange={onStageChange}
															isWhatsappEnabled={isWhatsappEnabled}
														/>
													</motion.tbody>
												</Fragment>
											))
										)
										: (
											<tbody className="divide-y divide-gray-200">
												<TableBody
													view={view}
													renderers={renderers}
													candidates={sortedCandidates}
													stages={view.stages}
													onStageChange={onStageChange}
													isWhatsappEnabled={isWhatsappEnabled}
												/>
											</tbody>
										)}
								</table>
							</div>
						)}
				</div>
				<SlideOverPanel open={!!view.selectedCandidate} onClose={onSlideOverClose}>
					{children}
				</SlideOverPanel>
				{onResetToDefaults && (
					<PipelineResetModal
						isOpen={showResetModal}
						onClose={handleResetModalClose}
						stagesToRemove={stagesToRemove}
						targetStages={resetTargetStages}
						onConfirm={handleResetConfirm}
						isResetting={loading.resetting ?? false}
						errorMessage={errors?.resetToDefaults || null}
					/>
				)}
				<>
					<BulkActionsBar
						stages={view.stages}
						sourceStages={sourceStages}
						onBulkMove={handleBulkMove}
						onBulkEmail={handleBulkEmail}
						onBulkWhatsApp={isWhatsappEnabled ? handleBulkWhatsApp : undefined}
						onBulkSendBookingLink={handleBulkSendBookingLink}
					/>
					<BulkEmailModal
						isOpen={showBulkEmailModal}
						onClose={() => setShowBulkEmailModal(false)}
						candidateIds={selectedIds}
						campaignId={view.campaign.id}
						onSuccess={handleBulkEmailSuccess}
					/>
					<BulkWhatsAppModal
						isOpen={showBulkWhatsAppModal}
						onClose={() => setShowBulkWhatsAppModal(false)}
						candidateIds={selectedIds}
						campaignId={view.campaign.id}
						onSuccess={handleBulkWhatsAppSuccess}
					/>
					<SendBookingLinkDialog
						isOpen={showSendBookingLinkModal}
						onClose={() => setShowSendBookingLinkModal(false)}
						campaignId={view.campaign.id}
						candidateIds={sendBookingLinkCandidateIds}
						isBulk
						onSuccess={handleBulkSendBookingLinkSuccess}
					/>
				</>
			</div>
		</CampaignNav>
	)
}

function TableHeader({ children }: { children: ReactNode }) {
	return (
		<motion.th
			layout="position"
			scope="col"
			className="py-3.5 px-3 text-left font-semibold"
		>
			<motion.span layout>{children}</motion.span>
		</motion.th>
	)
}

const tableCellClasses = ctl(`
	first:pl-2 whitespace-nowrap h-full bg-white
	group-data-[selected=true]:bg-purple-50/70 group-data-[selected=true]:text-purple-700
	group-data-[bulk-selected=true]:bg-purple-50/70 group-data-[bulk-selected=true]:text-purple-700
	transition-colors duration-50
`)

function TableCell({ children }: { children: (props: LinkLocalProps) => ReactNode }) {
	return (
		<td className={tableCellClasses}>
			<motion.div layout="position" className="grid w-full h-full">
				{children({
					className: 'py-6 px-3',
				})}
			</motion.div>
		</td>
	)
}

function TableBody({
	view,
	renderers,
	candidates,
	stages,
	onStageChange,
	isWhatsappEnabled,
}: {
	view: CampaignCandidatesViewModel['view']
	renderers: CampaignCandidatesViewModel['renderers']
	candidates: CandidateModel[]
	stages: CampaignCandidatesViewModel['view']['stages']
	onStageChange: (candidateIds: string[], stageId: string) => void
	isWhatsappEnabled: boolean
}) {
	const { t } = useTranslation('common')
	const selectedIds = useAtomValue(selectedCandidateIdsAtom)
	const toggleSelect = useSetAtom(toggleCandidateSelectionAtom)

	const cellWrapper = (candidateId: string, props: LinkLocalProps) => renderers.candidateLink(candidateId, props)

	const handleStageChange = (candidateId: string, stageId: string) => {
		if (selectedIds.has(candidateId)) {
			onStageChange(Array.from(selectedIds), stageId)
		} else {
			onStageChange([candidateId], stageId)
		}
	}

	return (
		candidates.map((candidate) => (
			<motion.tr
				layoutId={`candidate-${candidate.id}`}
				key={candidate.id}
				className="group"
				data-selected={candidate.id === view.selectedCandidate?.id}
				data-bulk-selected={selectedIds.has(candidate.id)}
			>
				<td className={tableCellClasses}>
					<motion.div layout="position" className="grid place-items-center h-full pl-4 pr-2">
						<SelectionCheckbox
							checked={selectedIds.has(candidate.id)}
							onToggle={() => toggleSelect(candidate.id)}
							label={t('candidates.selectCandidate')}
						/>
					</motion.div>
				</td>
				<TableCell>
					{(props) =>
						cellWrapper(candidate.id, {
							children: (
								<WidgetInputQuickSwitch
									options={(() => {
										// Generate options from stages, keeping Rejected at the end with divider
										// Use stage.id for option IDs to avoid collisions with user-defined names
										const nonRejected = stages.filter((s: { name: string }) =>
											s.name !== 'Rejected'
										)
										const rejected = stages.find((s: { id: string; name: string }) =>
											s.name === 'Rejected'
										)

										const options = nonRejected.map((stage: { id: string; name: string }) => ({
											id: stage.id,
											render: () => (
												<PipelineBadge stage={stage.name.toLowerCase()} name={stage.name} />
											),
										}))

										if (rejected) {
											options.push(
												widgetInputQuickSwitchDivider,
												{
													id: rejected.id,
													render: () => (
														<PipelineBadge
															stage={rejected.name.toLowerCase()}
															name={rejected.name}
														/>
													),
												},
											)
										}

										return options
									})()}
									value={candidate.stageId ?? ''}
									onChange={(stageId: string) => handleStageChange(candidate.id, stageId)}
									label={t('candidates.statusLabel')}
									placeholder={t('candidates.selectStatus')}
								/>
							),
							...props,
						})}
				</TableCell>
				<TableCell>
					{(props) =>
						cellWrapper(candidate.id, {
							children: (
								<span className="ph-no-capture font-medium group-data-[selected=true]:font-bold flex items-center gap-2">
									{candidate.name}
									{candidate.hasUnreadEmails && candidate.unreadEmailCount
										&& candidate.unreadEmailCount > 0 && (
										<Tooltip
											content={t('candidates.unreadEmails', {
												count: candidate.unreadEmailCount,
											})}
										>
											<span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
												<MailIcon className="size-3" />
												{candidate.unreadEmailCount}
											</span>
										</Tooltip>
									)}
									{isWhatsappEnabled && candidate.hasUnreadWhatsApp && candidate.unreadWhatsAppCount
										&& candidate.unreadWhatsAppCount > 0 && (
										<Tooltip
											content={t('candidates.unreadWhatsApp', {
												count: candidate.unreadWhatsAppCount,
											})}
										>
											<span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
												<MessageCircleIcon className="size-3" />
												{candidate.unreadWhatsAppCount}
											</span>
										</Tooltip>
									)}
									{candidate.notesCount != null && candidate.notesCount > 0 && (
										<NotesBadge count={candidate.notesCount} />
									)}
									<ProfileCountsBadge counts={candidate.profileCounts} />
								</span>
							),
							...props,
						})}
				</TableCell>
				<TableCell>
					{(props) =>
						cellWrapper(candidate.id, {
							children: (
								<span className="ph-no-capture group-data-[selected=true]:font-bold">
									{candidate.email}
								</span>
							),
							...props,
						})}
				</TableCell>
				<TableCell>
					{(props) =>
						cellWrapper(candidate.id, {
							children: (
								<span className="ph-no-capture group-data-[selected=true]:font-bold">
									{candidate.phone}
								</span>
							),
							...props,
						})}
				</TableCell>
				<TableCell>
					{(props) =>
						cellWrapper(candidate.id, {
							children: (
								<div className="">
									{candidate.disqualificationReason && candidate.score
										? (
											<Tooltip
												content={candidate.disqualificationReason}
											>
												<div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-600 text-white">
													<span className="font-medium">
														DQ (<span>
															{Math.round(
																candidate.score.percentage,
															)}%
														</span>)
													</span>
												</div>
											</Tooltip>
										)
										: candidate.score
										? (
											<div
												className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
												style={{
													backgroundColor: getScoreBackgroundColor(
														candidate.score.percentage,
													),
												}}
											>
												<span className="font-medium">
													{Math.round(
														candidate.score.percentage,
													)}%
												</span>
											</div>
										)
										: <span className="text-grey-400">&mdash;</span>}
								</div>
							),
							...props,
						})}
				</TableCell>
				<TableCell>
					{(props) =>
						cellWrapper(candidate.id, {
							children: (
								<div className="">
									{candidate.createdAt
										? <TimeAgo date={candidate.createdAt} short ago />
										: <span className="text-grey-400">&mdash;</span>}
								</div>
							),
							...props,
						})}
				</TableCell>
				<TableCell>
					{(props) =>
						cellWrapper(candidate.id, {
							children: <>&nbsp;</>,
							...props,
						})}
				</TableCell>
			</motion.tr>
		))
	)
}
